import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { PvpRestrictionTier } from '@/constance/pvpCardRestrictions'
import type { CardData } from '@/mockData/cardData'
import type { PvpTier, PvpSeasonPhase, PvpRecentMatch } from '@/services/pvp.service'

export interface PvpOpponent {
  userId: string
  name: string
  avatar: string
  squareAvatar: string
  level: number
  hp: number
  totalHp: number
  winRate: number
  pvpPoints: number
  rating?: number
  tier?: PvpTier
  division?: number
  divisionAp?: number
  isBot?: boolean
  // Bot gear-derived stat modifiers (BE pvp_bot_profile, 0..1 fractions).
  // Present only on bot opponents; the FE converts ×100 → percent and stores
  // them in botStore.botGear. Absent/undefined for human opponents (their gear
  // is applied authoritatively on their own device).
  damageIncrease?: number
  damageReduction?: number
  blockIncreaser?: number
  healIncrease?: number
}

export interface PvpRankedState {
  rating: number
  tier: PvpTier
  division: number
  divisionAp: number
  seasonalApEarned: number
  ap: number
  /** Live global rank across all tiers (1-based); null when not yet ranked. */
  rank: number | null
  totalGames: number
  placementGamesPlayed: number
  demotionShieldLosses: number
  inDemotionMatch: number
  currentSeasonId: number | null
  winStreak: number
  bestWinStreak: number
  // Where the player will land after their next-season placements complete.
  // Populated only during off-season + early next-season (until placements
  // complete and `finalizeSoftReset` commits these as the live tier/div).
  // Drives the s-12 soft-reset banner copy.
  pendingResetTier?: PvpTier | null
  pendingResetDivision?: number | null
}


export interface PvpSeasonReward {
  id: number
  type: 'gear' | 'cosmetic'
  name: string
  claimed: boolean
}

export interface PvpSeasonResult {
  seasonId: number
  finalRank: number
  peakTotalAp: number
  totalGames: number
  rewards: PvpSeasonReward[]
}

export interface PvpLeaderboardRow {
  fdvId: number
  name: string
  initials: string
  avatarImageUrl?: string | null
  tier: PvpTier
  division: number
  winRate: number
  wins: number
  losses: number
  totalAp: number
  apDelta: number
}

export interface PvpLeaderboardMe {
  rank: number
  fdvId: number
  totalAp: number
  apDeltaToday: number
  /** Positions gained today (positive = climbed); 0 when outside top-N. */
  rankDeltaToday: number
  tier?: PvpTier
}

export type PvpLeaderboardScope = 'mine' | 'global'

export interface PvpLeaderboardPayload {
  me: PvpLeaderboardMe
  top: PvpLeaderboardRow[]
  /** Tier the rows are filtered to (when scope='mine'); null in 'global'. */
  tier: PvpTier | null
  scope: PvpLeaderboardScope
}

export type PvpMyLeagueRange = 'III' | 'II' | 'I' | 'all'

export interface PvpMyLeagueDivision {
  tier: PvpTier
  divisionIndex: number
  population: number
}

export interface PvpMyLeagueMe {
  rank: number
  totalAp: number
  apToday: number
  divisionAp: number
  apLastMatch: number
}

export interface PvpMyLeagueRow {
  fdvId: number
  rank: number
  name: string
  initials: string
  avatarImageUrl?: string | null
  tier: PvpTier
  divisionAp: number
  totalAp: number
  apDelta: number
  rankDelta: number
  isMe?: boolean
}

export interface PvpMyLeaguePayload {
  division: PvpMyLeagueDivision
  me: PvpMyLeagueMe
  window: PvpMyLeagueRow[]
}

export interface PvpPlacementSlot {
  index: number
  result: 'win' | 'loss' | 'draw' | null
}

export interface PvpPlacementsPayload {
  seasonNumber: number
  dayInSeason: number
  matchesPlayed: number
  totalMatches: number
  results: PvpPlacementSlot[]
  projectedTier: PvpTier
  projectedDivision: number
  projectedMmr: number
  demotionShieldActive: boolean
  shieldLossesRemaining: number
}

export type PvpMilestoneState = 'locked' | 'claimable' | 'claimed'

export interface PvpMilestoneReward {
  rewardType: string
  name: string
  amount: number
  label?: string
  image?: string
}

export interface PvpMilestoneChoice {
  key: string
  label: string
  rewards: PvpMilestoneReward[]
}

export interface PvpMilestone {
  level: number
  name: string
  thresholdAp: number
  state: PvpMilestoneState
  rewards: PvpMilestoneReward[]
  choices?: PvpMilestoneChoice[]
}

export interface PvpMilestonesPayload {
  ap: number
  milestones: PvpMilestone[]
}

export interface PvpTierPromotionPayload {
  prevTier: PvpTier
  prevDivision: number
  newTier: PvpTier
  newDivision: number
  percentile: number
  shieldLossesRemaining: number
}

export type PvpMatchHistoryFilter = 'all' | 'wins' | 'losses'

export interface PvpMatchHistoryOverview {
  seasonNumber: number
  windowDays: number
  totalGames: number
  wins: number
  losses: number
  winRate: number
  apNet: number
}

export interface PvpMatchHistoryItem {
  matchId: string
  result: 'win' | 'loss' | 'draw'
  opponentName: string
  opponentTier: PvpTier
  opponentAp: number
  apDelta: number
  isMvp?: boolean
  isBot?: boolean
  finishedAt: string
}

export interface PvpMatchHistoryCounts {
  all: number
  wins: number
  losses: number
}

export interface PvpMatchHistoryPayload {
  overview: PvpMatchHistoryOverview
  counts: PvpMatchHistoryCounts
  items: PvpMatchHistoryItem[]
}

export interface PvpCardSelection {
  cardAssetId: string
  cardMasterId: string
  cardId: string
  name: string
  imageUrl: string
  type: string
  cardType: string
  ownedQuantity: number
  isBanned: boolean
  restrictionTier: PvpRestrictionTier
  maxCopies: number
}

export interface SavedStrategy {
  id: string
  name: string
  cards: { cardAssetId: string; quantity: number }[]
  createdAt?: string
}

export interface PvpSelectedCard {
  cardAssetId: string
  quantity: number
  multiplier: number
}

export type PvpPhase =
  | 'idle'
  | 'onboarding'
  | 'deck-builder-first'
  | 'loadout-intro'
  | 'lobby-home'
  | 'rank-detail'
  | 'league-tiers'
  | 'leaderboard'
  | 'my-league'
  | 'placements'
  | 'milestones'
  | 'tier-promotion'
  | 'match-history'
  | 'deck-builder'
  | 'loadout-builder'
  | 'queuing'
  | 'finding'
  | 'found'
  | 'card-selection'
  | 'pre-battle-selection'
  | 'ready-wait'
  | 'ready'
  | 'starting'
  | 'error'

export type PvpLoadoutSlotType =
  | 'head'
  | 'chest'
  | 'pants'
  | 'boots'
  | 'gloves'
  | 'cloak'
  | 'weapon'
  | 'accessory'

export interface PvpDeckCard {
  cardAssetId: string
  quantity: number
}

export interface PvpDeck {
  id: number
  name: string
  cards: PvpDeckCard[]
  lastUsedAt: string | null
  createdAt?: string
  updatedAt?: string
  isValid?: boolean
  invalidReason?: string
  totalCount?: number
}

export interface PvpLoadoutSlotItem {
  slot: PvpLoadoutSlotType
  gamificationAssetId: string
  contractAddress: string
  tokenId: string
}

export interface PvpLoadout {
  id: number
  name: string
  isAutoGenerated: boolean
  slots: PvpLoadoutSlotItem[]
  lastUsedAt: string | null
  createdAt?: string
  updatedAt?: string
  isValid?: boolean
  invalidReason?: string
}

export interface PvpOnboardingStatus {
  completed: boolean
  hasFirstDeck: boolean
  hasFirstLoadout: boolean
  completedAt: string | null
}

interface PvpLobbyState {
  phase: PvpPhase
  matchId: string | null
  roomId: string | null
  channelName: string | null
  error: string | null
  opponent: PvpOpponent | null
  availableCards: PvpCardSelection[]
  selectedCards: Map<string, PvpSelectedCard>
  totalSelectedCount: number
  maxCards: 100
  bannedCardIds: string[]
  savedStrategies: SavedStrategy[]
  activeStrategyId: string | null
  myPvpPoints: number
  myWinRate: number
  isReady: boolean
  opponentReady: boolean
  // Full card catalogue from getCards, keyed by cardAssetId (== CardData.id)
  allCardData: Record<string, CardData>
  // Replicated CardData passed to the game when selection is confirmed
  selectedCardsData: CardData[]

  // ─── Deck / loadout / onboarding (v2) ──────────────────────────────────
  onboardingStatus: PvpOnboardingStatus | null
  decks: PvpDeck[]
  activeDeckId: number | null
  loadouts: PvpLoadout[]
  activeLoadoutId: number | null
  selectionDeadlineAt: string | null
  opponentSelectionLocked: boolean
  // Wall-clock timestamp set when the player joined the matchmaking queue;
  // null when not queueing. FindingMatchView derives the visible "elapsed"
  // counter from `Date.now() - queueStartedAt` rather than holding it in
  // useState — this keeps the counter monotonic across React Strict Mode's
  // dev-mode unmount/remount and across phase-driven view swaps.
  queueStartedAt: number | null

  // ─── Ranked league ─────────────────────────────────────────────────────
  ranked: PvpRankedState | null
  seasonPhase: PvpSeasonPhase | null
  lastDemotionMatchTriggered: boolean
  lastDemotionMatchOutcome: 'survived' | 'demoted' | null
  lastTierChange: PvpTier | null
  recentMatches: PvpRecentMatch[]
  seasonResult: PvpSeasonResult | null
  leaderboard: PvpLeaderboardPayload | null
  setLeaderboard: (payload: PvpLeaderboardPayload | null) => void
  myLeague: PvpMyLeaguePayload | null
  myLeagueRange: PvpMyLeagueRange
  /** Tier currently shown in My League view's top-100 tab. null = user's own league. */
  myLeagueActiveTier: PvpTier | null
  setMyLeagueActiveTier: (t: PvpTier | null) => void
  setMyLeague: (payload: PvpMyLeaguePayload | null) => void
  setMyLeagueRange: (range: PvpMyLeagueRange) => void
  enterMyLeague: () => void
  exitMyLeague: () => void
  placements: PvpPlacementsPayload | null
  setPlacements: (payload: PvpPlacementsPayload | null) => void
  enterPlacements: () => void
  exitPlacements: () => void
  milestones: PvpMilestone[]
  ap: number
  setMilestones: (payload: PvpMilestonesPayload) => void
  enterMilestones: () => void
  exitMilestones: () => void
  tierPromotion: PvpTierPromotionPayload | null
  showTierPromotion: (payload: PvpTierPromotionPayload) => void
  dismissTierPromotion: () => void
  matchHistory: PvpMatchHistoryPayload | null
  matchHistoryFilter: PvpMatchHistoryFilter
  matchHistoryOrigin: 'lobby-home' | 'rank-detail'
  setMatchHistory: (payload: PvpMatchHistoryPayload | null) => void
  setMatchHistoryFilter: (filter: PvpMatchHistoryFilter) => void
  enterMatchHistory: (origin?: 'lobby-home' | 'rank-detail') => void
  exitMatchHistory: () => void
  setSeasonResult: (result: PvpSeasonResult | null) => void
  setRecentMatches: (matches: PvpRecentMatch[]) => void
  enterRankDetail: () => void
  exitRankDetail: () => void
  enterLeagueTiers: () => void
  exitLeagueTiers: () => void
  enterLeaderboard: () => void
  enterLeagueLeaderboard: (tier: PvpTier) => void
  exitLeaderboard: () => void
  /** When set, leaderboard view filters to this tier instead of caller's. */
  leaderboardTierOverride: PvpTier | null
  setRanked: (ranked: PvpRankedState | null) => void
  setSeasonPhase: (phase: PvpSeasonPhase | null) => void
  applyRankedUpdate: (update: {
    ranked: PvpRankedState
    demotionMatchTriggered?: boolean
    demotionMatchOutcome?: 'survived' | 'demoted' | null
    tierChangedTo?: PvpTier | null
  }) => void
  clearDemotionFlags: () => void
  clearTierChange: () => void

  setOnboardingStatus: (status: PvpOnboardingStatus | null) => void
  setDecks: (decks: PvpDeck[]) => void
  setActiveDeckId: (id: number | null) => void
  setLoadouts: (loadouts: PvpLoadout[]) => void
  setActiveLoadoutId: (id: number | null) => void
  setSelectionDeadline: (deadlineIso: string | null) => void
  setOpponentSelectionLocked: (locked: boolean) => void
  advanceToLobbyHome: () => void
  enterDeckBuilder: () => void
  enterLoadoutBuilder: () => void
  enterQueuing: () => void
  enterPreBattleSelection: (deadlineIso: string | null) => void

  setPhase: (phase: PvpPhase) => void
  setMatchFound: (data: {
    matchId: string
    roomId: string
    channelName: string
    opponent: PvpOpponent
  }) => void
  setAvailableCards: (cards: PvpCardSelection[]) => void
  toggleCardSelection: (cardAssetId: string) => void
  setCardQuantity: (cardAssetId: string, quantity: number) => void
  loadStrategy: (strategy: SavedStrategy) => void
  setSavedStrategies: (strategies: SavedStrategy[]) => void
  setReady: (ready: boolean) => void
  setOpponentReady: (ready: boolean) => void
  setMyPvpPoints: (points: number, winRate?: number) => void
  setError: (error: string | null) => void
  resetLobby: () => void
  setQueueStartedAt: (ts: number | null) => void
  setAllCardData: (cards: CardData[]) => void
  commitSelectionForGame: () => void
}

const computeTotal = (map: Map<string, PvpSelectedCard>): number => {
  let total = 0
  map.forEach((card) => {
    total += card.multiplier
  })
  return total
}

const usePvpLobbyStore = create<PvpLobbyState>()(
  persist(
    (set, get) => ({
      phase: 'idle',
      matchId: null,
      roomId: null,
      channelName: null,
      error: null,
      opponent: null,
      availableCards: [],
      selectedCards: new Map(),
      totalSelectedCount: 0,
      maxCards: 100,
      bannedCardIds: [],
      savedStrategies: [],
      activeStrategyId: null,
      myPvpPoints: 0,
      myWinRate: 0,
      isReady: false,
      opponentReady: false,
      allCardData: {},
      selectedCardsData: [],

      onboardingStatus: null,
      decks: [],
      activeDeckId: null,
      loadouts: [],
      activeLoadoutId: null,
      selectionDeadlineAt: null,
      opponentSelectionLocked: false,
      queueStartedAt: null,

      ranked: null,
      seasonPhase: null,
      lastDemotionMatchTriggered: false,
      lastDemotionMatchOutcome: null,
      lastTierChange: null,
      recentMatches: [],
      seasonResult: null,
      leaderboard: null,
      myLeague: null,
      myLeagueRange: 'II',
      myLeagueActiveTier: null,
      placements: null,
      milestones: [],
      ap: 0,
      tierPromotion: null,
      matchHistory: null,
      matchHistoryFilter: 'all',
      matchHistoryOrigin: 'rank-detail',

      setLeaderboard: (payload) => set({ leaderboard: payload }),
      setMyLeague: (payload) => set({ myLeague: payload }),
      setMyLeagueRange: (range) => set({ myLeagueRange: range }),
      setMyLeagueActiveTier: (t) => set({ myLeagueActiveTier: t }),
      enterMyLeague: () => set({ phase: 'my-league' }),
      exitMyLeague: () => set({ phase: 'rank-detail', myLeagueActiveTier: null }),
      setPlacements: (payload) => set({ placements: payload }),
      enterPlacements: () => set({ phase: 'placements' }),
      exitPlacements: () => set({ phase: 'lobby-home' }),
      setMilestones: (payload) =>
        set({ milestones: payload.milestones, ap: payload.ap }),
      enterMilestones: () => set({ phase: 'milestones' }),
      exitMilestones: () => set({ phase: 'rank-detail' }),
      showTierPromotion: (payload) =>
        set({ tierPromotion: payload, phase: 'tier-promotion' }),
      dismissTierPromotion: () =>
        set({ tierPromotion: null, phase: 'rank-detail' }),
      setMatchHistory: (payload) => set({ matchHistory: payload }),
      setMatchHistoryFilter: (filter) => set({ matchHistoryFilter: filter }),
      enterMatchHistory: (origin = 'rank-detail') =>
        set({ phase: 'match-history', matchHistoryOrigin: origin }),
      exitMatchHistory: () => set({ phase: get().matchHistoryOrigin }),
      setSeasonResult: (result) => set({ seasonResult: result }),
      setRecentMatches: (matches) => set({ recentMatches: matches }),
      enterRankDetail: () => set({ phase: 'rank-detail' }),
      exitRankDetail: () => set({ phase: 'lobby-home' }),
      enterLeagueTiers: () => set({ phase: 'league-tiers' }),
      exitLeagueTiers: () => set({ phase: 'rank-detail' }),
      enterLeaderboard: () =>
        set({ phase: 'leaderboard', leaderboardTierOverride: null }),
      enterLeagueLeaderboard: (tier) =>
        set({ phase: 'leaderboard', leaderboardTierOverride: tier }),
      // If we were peeking at a specific league (override set), back should
      // return to the league-tiers list (where the player tapped to get here).
      // Otherwise return to the lobby home as before.
      exitLeaderboard: () =>
        set((s) => ({
          phase: s.leaderboardTierOverride ? 'league-tiers' : 'lobby-home',
          leaderboardTierOverride: null,
        })),
      leaderboardTierOverride: null,

      setRanked: (ranked) => set({ ranked }),
      setSeasonPhase: (phase) => set({ seasonPhase: phase }),
      applyRankedUpdate: ({
        ranked,
        demotionMatchTriggered,
        demotionMatchOutcome,
        tierChangedTo,
      }) => {
        set({
          ranked,
          lastDemotionMatchTriggered: !!demotionMatchTriggered,
          lastDemotionMatchOutcome: demotionMatchOutcome ?? null,
          lastTierChange: tierChangedTo ?? null,
        })
      },
      clearDemotionFlags: () =>
        set({ lastDemotionMatchTriggered: false, lastDemotionMatchOutcome: null }),
      clearTierChange: () => set({ lastTierChange: null }),

      setOnboardingStatus: (status) => set({ onboardingStatus: status }),
      setDecks: (decks) => set({ decks }),
      setActiveDeckId: (id) => set({ activeDeckId: id }),
      setLoadouts: (loadouts) => set({ loadouts }),
      setActiveLoadoutId: (id) => set({ activeLoadoutId: id }),
      setSelectionDeadline: (deadlineIso) => set({ selectionDeadlineAt: deadlineIso }),
      setOpponentSelectionLocked: (locked) => set({ opponentSelectionLocked: locked }),
      advanceToLobbyHome: () => set({ phase: 'lobby-home', error: null }),
      enterDeckBuilder: () => set({ phase: 'deck-builder' }),
      enterLoadoutBuilder: () => set({ phase: 'loadout-builder' }),
      // Clear prior-match state so the radar always starts in `searching`.
      // `opponent` is persisted (see partialize) and lingers after a finished
      // match or a page reload — without nulling it here, FindingMatchView
      // seeds `locking=true` and jumps straight to the CHALLENGER LOCKED
      // screen on re-queue. Lock should only snap when a fresh setMatchFound
      // arrives.
      enterQueuing: () =>
        set({
          phase: 'queuing',
          error: null,
          opponent: null,
          matchId: null,
          roomId: null,
          channelName: null,
        }),
      enterPreBattleSelection: (deadlineIso) =>
        set({ phase: 'pre-battle-selection', selectionDeadlineAt: deadlineIso }),

      setPhase: (phase) => set({ phase }),

      setMatchFound: ({ matchId, roomId, channelName, opponent }) =>
        set({ matchId, roomId, channelName, opponent, phase: 'found', error: null }),

      setAvailableCards: (cards) => {
        const bannedCardIds = cards.filter((c) => c.isBanned).map((c) => c.cardAssetId)
        set({ availableCards: cards, bannedCardIds })
      },

      toggleCardSelection: (cardAssetId) => {
        const { selectedCards, maxCards } = get()
        const next = new Map(selectedCards)
        if (next.has(cardAssetId)) {
          next.delete(cardAssetId)
        } else {
          const total = computeTotal(next)
          if (total >= maxCards) return
          next.set(cardAssetId, { cardAssetId, quantity: 1, multiplier: 1 })
        }
        set({ selectedCards: next, totalSelectedCount: computeTotal(next) })
      },

      setCardQuantity: (cardAssetId, quantity) => {
        const { selectedCards, maxCards, availableCards } = get()
        const cardDef = availableCards.find((c) => c.cardAssetId === cardAssetId)
        const perCardMax = cardDef?.maxCopies ?? 4
        const clamped = Math.max(1, Math.min(perCardMax, quantity))
        const next = new Map(selectedCards)
        const existing = next.get(cardAssetId)
        if (!existing) return

        const totalWithout = computeTotal(next) - existing.multiplier
        const allowed = Math.min(clamped, maxCards - totalWithout)
        if (allowed < 1) return

        next.set(cardAssetId, { ...existing, multiplier: allowed })
        set({ selectedCards: next, totalSelectedCount: computeTotal(next) })
      },

      loadStrategy: (strategy) => {
        const { availableCards, bannedCardIds, maxCards } = get()
        const ownedIds = new Set(availableCards.map((c) => c.cardAssetId))
        const cardDefMap = new Map(availableCards.map((c) => [c.cardAssetId, c]))
        const next = new Map<string, PvpSelectedCard>()

        for (const card of strategy.cards) {
          if (!ownedIds.has(card.cardAssetId)) continue
          if (bannedCardIds.includes(card.cardAssetId)) continue
          const cardDef = cardDefMap.get(card.cardAssetId)
          const perCardMax = cardDef?.maxCopies ?? 4
          const total = computeTotal(next)
          const clamped = Math.max(1, Math.min(perCardMax, card.quantity))
          const allowed = Math.min(clamped, maxCards - total)
          if (allowed < 1) break
          next.set(card.cardAssetId, {
            cardAssetId: card.cardAssetId,
            quantity: card.quantity,
            multiplier: allowed,
          })
        }

        set({ selectedCards: next, totalSelectedCount: computeTotal(next), activeStrategyId: strategy.id })
      },

      setSavedStrategies: (strategies) => set({ savedStrategies: strategies }),

      setReady: (ready) => set({ isReady: ready, phase: ready ? 'ready' : 'card-selection' }),

      setOpponentReady: (ready) => set({ opponentReady: ready }),

      setMyPvpPoints: (points, winRate) =>
        set({ myPvpPoints: points, ...(winRate !== undefined ? { myWinRate: winRate } : {}) }),

      setError: (error) => set({ error, phase: error ? 'error' : get().phase }),

      setAllCardData: (cards) => {
        const byId: Record<string, CardData> = {}
        cards.forEach((c) => {
          byId[c.id] = c
        })
        set({ allCardData: byId })
      },

      commitSelectionForGame: () => {
        const { selectedCards, allCardData } = get()
        const replicated: CardData[] = []
        selectedCards.forEach((sel) => {
          const card = allCardData[sel.cardAssetId]
          if (!card) return
          for (let i = 0; i < sel.multiplier; i++) replicated.push(card)
        })
        set({ selectedCardsData: replicated })
      },

      resetLobby: () =>
        set({
          phase: 'idle',
          matchId: null,
          roomId: null,
          channelName: null,
          error: null,
          opponent: null,
          availableCards: [],
          selectedCards: new Map(),
          totalSelectedCount: 0,
          bannedCardIds: [],
          activeStrategyId: null,
          isReady: false,
          opponentReady: false,
          selectedCardsData: [],
          selectionDeadlineAt: null,
          opponentSelectionLocked: false,
          queueStartedAt: null,
        }),

      setQueueStartedAt: (ts) => set({ queueStartedAt: ts }),
    }),
    {
      name: 'pvp-lobby-storage',
      partialize: (state) => ({
        matchId: state.matchId,
        // Persist the opponent alongside matchId so a mid-match RELOAD (webview
        // refresh, dev HMR full-reload, post-deploy chunk reload) doesn't strand
        // bot matches. RoomController's presence gate clears immediately for bot
        // opponents (it pre-seeds the server-side bot, which never joins
        // Centrifuge presence) by reading `opponent?.isBot`. Without persistence
        // that flag is null after a reload, so the gate falls back to waiting
        // for two real Centrifuge clients — the bot never joins → infinite
        // "waiting for opponent" spinner. Only read when matchId is set (also
        // persisted); setMatchFound overwrites it and resetLobby nulls it.
        opponent: state.opponent,
        channelName: state.channelName,
        roomId: state.roomId,
        activeDeckId: state.activeDeckId,
        activeLoadoutId: state.activeLoadoutId,
        // Persist onboardingStatus so a returning player isn't dropped
        // back into the s-14→s-33 flow if the BE complete-POST hasn't
        // landed yet (offline, race with navigation back to the lobby).
        // usePvpOnboarding still refreshes from the BE on mount — this
        // is just the fallback when the BE still reports completed:false.
        onboardingStatus: state.onboardingStatus,
      }),
    },
  ),
)

export default usePvpLobbyStore
